import { Avatar, AvatarGroup, Box, Button, Card, CardContent, Divider, IconButton, LinearProgress, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import { IoArrowForward, IoTrashOutline } from 'react-icons/io5';
import palette from 'src/theme/palette';
import { RoleDefinition } from 'src/types/permissions.types';
import { ALL_PERMISSIONS } from 'src/config/permissionCatalog';
import { RoleMember } from 'src/services/role.service';
import { personName, roleAccent, RoleKindChip, ShellChip } from './RoleUi';

interface RoleCardProps {
  role: RoleDefinition;
  members: RoleMember[];
  onOpen: () => void;
  onDelete?: () => void;
}

export function RoleCard({ role, members, onOpen, onDelete }: RoleCardProps) {
  const accent = roleAccent(role);
  const isWildcard = role.permissions.includes('*');
  const granted = isWildcard ? ALL_PERMISSIONS.length : role.permissions.filter((p) => p !== '*').length;
  const coverage = Math.round((granted / ALL_PERMISSIONS.length) * 100);

  return (
    <Card
      elevation={0}
      onClick={onOpen}
      sx={{
        borderRadius: 2.5,
        border: '1px solid #E2E8F0',
        bgcolor: '#FFFFFF',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          borderColor: '#CBD5E0',
          boxShadow: '0 6px 20px rgba(15, 23, 42, 0.07)',
          transform: 'translateY(-2px)',
          '& .role-arrow-btn': {
            bgcolor: alpha(palette.primary.main, 0.12),
            color: palette.primary.main,
            transform: 'translateX(2px)',
          },
        },
      }}
    >
      <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1, '&:last-child': { pb: 2 } }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.25 }}>
          <Stack direction="row" spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                flexShrink: 0,
                borderRadius: 2,
                bgcolor: alpha(accent.color, 0.12),
                color: accent.color,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Icon icon={isWildcard ? 'solar:crown-bold-duotone' : 'solar:shield-user-bold-duotone'} width={20} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 0.25 }}>
                <Typography variant="subtitle1" fontWeight={800} color="#0F172A" lineHeight={1.2}>
                  {role.label}
                </Typography>
                <RoleKindChip role={role} />
              </Stack>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  fontSize: '0.78125rem',
                  minHeight: '2.2em',
                  lineHeight: 1.35,
                }}
              >
                {role.description || 'No description yet.'}
              </Typography>
            </Box>
          </Stack>

          {onDelete && (
            <Tooltip title="Delete role">
              <IconButton
                size="small"
                aria-label={`Delete ${role.label}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                sx={{ p: 0.5, color: '#EF4444', borderRadius: 1.25, '&:hover': { bgcolor: '#FEF2F2', color: '#DC2626' } }}
              >
                <IoTrashOutline size={15} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        {/* Metrics strip */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.5 }}>
          <Box sx={{ p: 1.25, bgcolor: '#F8FAFC', borderRadius: 1.75, border: '1px solid #F1F5F9' }}>
            <Typography variant="caption" fontWeight={700} color="#64748B" fontSize="0.65rem" letterSpacing={0.3}>
              MEMBERS
            </Typography>
            <Typography variant="subtitle2" fontWeight={800} color="#0F172A" lineHeight={1.1}>
              {members.length}{' '}
              <Typography component="span" variant="caption" color="text.secondary" fontWeight={500} fontSize="0.7rem">
                {members.length === 1 ? 'person' : 'people'}
              </Typography>
            </Typography>
          </Box>
          <Box sx={{ p: 1.25, bgcolor: '#F8FAFC', borderRadius: 1.75, border: '1px solid #F1F5F9' }}>
            <Typography variant="caption" fontWeight={700} color="#64748B" fontSize="0.65rem" letterSpacing={0.3}>
              PERMISSIONS
            </Typography>
            <Typography variant="subtitle2" fontWeight={800} color="#0F172A" lineHeight={1.1}>
              {isWildcard ? 'All' : granted}{' '}
              <Typography component="span" variant="caption" color="text.secondary" fontWeight={500} fontSize="0.7rem">
                of {ALL_PERMISSIONS.length}
              </Typography>
            </Typography>
            <LinearProgress
              variant="determinate"
              value={coverage}
              sx={{
                mt: 0.75,
                height: 4,
                borderRadius: 2,
                bgcolor: '#E2E8F0',
                '& .MuiLinearProgress-bar': { bgcolor: accent.color, borderRadius: 2 },
              }}
            />
          </Box>
        </Box>

        {/* Dashboard + member avatars */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5, mt: 'auto', minHeight: 26 }}>
          <ShellChip role={role} />
          {members.length > 0 ? (
            <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 22, height: 22, fontSize: '0.6875rem' } }}>
              {members.slice(0, 4).map((m) => (
                <Tooltip key={m.id} title={personName(m)}>
                  <Avatar sx={{ bgcolor: alpha(accent.color, 0.15), color: accent.color, fontWeight: 700 }}>
                    {(m.firstname || m.email)[0]?.toUpperCase()}
                  </Avatar>
                </Tooltip>
              ))}
            </AvatarGroup>
          ) : (
            <Typography variant="caption" color="text.secondary" fontStyle="italic" fontSize="0.7rem">
              Nobody yet
            </Typography>
          )}
        </Stack>

        <Divider sx={{ mb: 1.25, borderColor: '#F1F5F9' }} />

        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary" fontWeight={600} fontSize="0.7rem" sx={{ fontFamily: 'monospace' }}>
            {role.name}
          </Typography>
          <Button
            size="small"
            className="role-arrow-btn"
            endIcon={<IoArrowForward size={13} />}
            sx={{
              fontWeight: 700,
              fontSize: '0.725rem',
              color: '#0F172A',
              bgcolor: '#F1F5F9',
              borderRadius: 1.5,
              px: 1.25,
              py: 0.35,
              transition: 'all 0.2s ease',
              textTransform: 'none',
            }}
          >
            Workspace
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
